/**
 * Data-driven description of the "Create / Edit User Role" feature-selection UI.
 *
 * One `ModuleConfig` per module; `ModuleFeatureSelector` renders a module card purely from
 * one of these objects. Mirrors the model already used by the mobile app
 * (`cowallet-mobile-app/src/components/administration/moduleGroupConfigs.ts`) so the two
 * platforms stay in sync.
 *
 * Labels, groupings and dependency hints here are transcribed from the (now removed) 20
 * `*ModuleAndFeatures.tsx` components. Leaf checkbox labels are NOT defined here — the
 * component uses the feature's `name` from the API, matching the old behaviour.
 */
import {
  // Dashboard
  DASHBOARD_MODULE,
  VIEW_BALANCE_AND_ADVANCE_SUMMARY,
  VIEW_PROJECT_SUMMARY,
  VIEW_EXPENSE_SUMMARY,
  // Dashboard Pro
  DASHBOARD_PRO_MODULE,
  VIEW_PRO_BALANCE_AND_ADVANCE_SUMMARY,
  VIEW_PRO_PROJECT_SUMMARY,
  VIEW_PRO_EXPENSE_SUMMARY,
  VIEW_PRO_PNL_SUMMARY,
  VIEW_PRO_DAILY_TRANSACTIONS,
  VIEW_PRO_DELAYED_PROJECTS,
  VIEW_PRO_MONTHLY_INCOME_EXPENSE,
  VIEW_PRO_TASK_SUMMARY,
  // CRM
  CRM_MODULE,
  CRM_VIEW_ALL_LEAD,
  CRM_VIEW_ASSIGNED_LEAD,
  CRM_CREATE_LEAD,
  CRM_EDIT_LEAD,
  CRM_DELETE_LEAD,
  CRM_EXPORT_LEAD,
  CRM_CREATE_LEAD_STATUS,
  CRM_EDIT_LEAD_STATUS,
  CRM_DELETE_LEAD_STATUS,
  // CRM Pro
  CRM_PRO_MODULE,
  CRM_PRO_VIEW_ALL_LEAD,
  CRM_PRO_VIEW_ASSIGNED_LEAD,
  CRM_PRO_CREATE_LEAD,
  CRM_PRO_EDIT_LEAD,
  CRM_PRO_DELETE_LEAD,
  CRM_PRO_EXPORT_LEAD,
  CRM_PRO_CREATE_LEAD_STATUS,
  CRM_PRO_EDIT_LEAD_STATUS,
  CRM_PRO_DELETE_LEAD_STATUS,
  CRM_PRO_CREATE_TASK,
  CRM_PRO_EDIT_TASK,
  CRM_PRO_DELETE_TASK,
  CRM_PRO_UPDATE_TASK_PROGRESS,
  CRM_PRO_UPLOAD_DOCUMENT,
  CRM_PRO_DELETE_DOCUMENT,
  // Project
  PROJECT_MODULE,
  VIEW_PROJECT,
  CREATE_PROJECT,
  EDIT_PROJECT,
  DELETE_PROJECT,
  EXPORT_PROJECT,
  VIEW_STAGE,
  CREATE_STAGE,
  EDIT_STAGE,
  DELETE_STAGE,
  // Client
  CLIENT_MODULE,
  VIEW_CLIENT,
  CREATE_CLIENT,
  EDIT_CLIENT,
  DELETE_CLIENT,
  EXPORT_CLIENT,
  ADD_CLIENT_INCOME,
  EDIT_CLIENT_INCOME,
  DELETE_CLIENT_INCOME,
  ENABLE_CLIENT_INCOME_BACKDATE_ENTRY,
  ADD_CLIENT_PAYMENT,
  EDIT_CLIENT_PAYMENT,
  DELETE_CLIENT_PAYMENT,
  ENABLE_CLIENT_PAYMENT_BACKDATE_ENTRY,
  VIEW_CLIENT_STATEMENT,
  VIEW_CLIENT_LEDGER_MODE_STATEMENT,
  EXPORT_CLIENT_STATEMENT,
  // Employee
  EMPLOYEE_MODULE,
  VIEW_EMPLOYEE,
  CREATE_EMPLOYEE,
  EDIT_EMPLOYEE,
  DELETE_EMPLOYEE,
  EXPORT_EMPLOYEE,
  ADD_EMPLOYEE_ATTENDANCE,
  EDIT_EMPLOYEE_ATTENDANCE,
  DELETE_EMPLOYEE_ATTENDANCE,
  ADD_EMPLOYEE_SALARY,
  EDIT_EMPLOYEE_SALARY,
  DELETE_EMPLOYEE_SALARY,
  ENABLE_EMPLOYEE_SALARY_BACKDATE_ENTRY,
  ADD_EMPLOYEE_INCENTIVE,
  EDIT_EMPLOYEE_INCENTIVE,
  DELETE_EMPLOYEE_INCENTIVE,
  VIEW_EMPLOYEE_STATEMENT,
  EXPORT_EMPLOYEE_STATEMENT,
  // Worker
  WORKER_MODULE,
  VIEW_WORKER,
  CREATE_WORKER,
  EDIT_WORKER,
  DELETE_WORKER,
  EXPORT_WORKER,
  ADD_WORK,
  ADD_BULK_WORK,
  EDIT_WORK,
  EDIT_BULK_WORK,
  DELETE_WORK,
  VIEW_GROUP_WORK,
  ADD_GROUP_WORK,
  EDIT_GROUP_WORK,
  DELETE_GROUP_WORK,
  ENABLE_WORK_BACKDATE_ENTRY,
  ADD_WORKER_PAYMENT,
  EDIT_WORKER_PAYMENT,
  DELETE_WORKER_PAYMENT,
  ENABLE_WORKER_PAYMENT_BACKDATE_ENTRY,
  ENABLE_WORKER_ADVANCE_PAYMENT,
  VIEW_WORKER_STATEMENT,
  VIEW_WORKER_LEDGER_MODE_STATEMENT,
  EXPORT_WORKER_STATEMENT,
  // Weekly Worker Payment
  WEEKLY_WORKER_PAYMENT_MODULE,
  VIEW_WEEKLY_WORKER_PAYMENT,
  ADD_WEEKLY_WORKER_PAYMENT,
  EDIT_WEEKLY_WORKER_PAYMENT,
  DELETE_WEEKLY_WORKER_PAYMENT,
  VIEW_WEEKLY_WORKER_PAYMENT_REPORT,
  PAY_WEEKLY_WORKER_PAYMENT,
  ASSIGN_WEEKLY_WORKER_PAYMENT_SUPERVISOR,
  EXPORT_WEEKLY_WORKER_PAYMENT,
  VIEW_WORKER_WELFARE,
  CREATE_WORKER_WELFARE,
  EDIT_WORKER_WELFARE,
  PAY_WORKER_WELFARE,
  // Subcontractor
  SUBCONTRACTOR_MODULE,
  VIEW_SUBCONTRACTOR,
  CREATE_SUBCONTRACTOR,
  EDIT_SUBCONTRACTOR,
  DELETE_SUBCONTRACTOR,
  EXPORT_SUBCONTRACTOR,
  VIEW_CONTRACT,
  CREATE_CONTRACT,
  EDIT_CONTRACT,
  DELETE_CONTRACT,
  ENABLE_CONTRACT_BACKDATE_ENTRY,
  EXPORT_CONTRACT,
  CREATE_SUBCONTRACTOR_TRANSACTION,
  EDIT_SUBCONTRACTOR_TRANSACTION,
  DELETE_SUBCONTRACTOR_TRANSACTION,
  VIEW_SUBCONTRACTOR_STATEMENT,
  VIEW_SUBCONTRACTOR_LEDGER_MODE_STATEMENT,
  ENABLE_SUBCONTRACTOR_TRANSACTION_BACKDATE_ENTRY,
  ENABLE_SUBCONTRACTOR_ADVANCE_PAYMENT,
  EXPORT_SUBCONTRACTOR_TRANSACTION,
  // Vendor
  VENDOR_MODULE,
  VIEW_VENDOR,
  CREATE_VENDOR,
  EDIT_VENDOR,
  DELETE_VENDOR,
  EXPORT_VENDOR,
  CREATE_PURCHASE,
  EDIT_PURCHASE,
  RETURN_PURCHASE,
  DELETE_PURCHASE,
  DELETE_PURCHASE_RETURN,
  EXPORT_PURCHASE,
  ENABLE_PURCHASE_BACKDATE_ENTRY,
  VIEW_MATERIAL,
  CREATE_MATERIAL,
  EDIT_MATERIAL,
  DELETE_MATERIAL,
  ADD_VENDOR_PAYMENT,
  EDIT_VENDOR_PAYMENT,
  DELETE_VENDOR_PAYMENT,
  ENABLE_VENDOR_PAYMENT_BACKDATE_ENTRY,
  ENABLE_VENDOR_ADVANCE_PAYMENT,
  VIEW_VENDOR_STATEMENT,
  VIEW_VENDOR_LEDGER_MODE_STATEMENT,
  EXPORT_VENDOR_STATEMENT,
  // User
  USER_MODULE,
  VIEW_USER,
  CREATE_USER,
  EDIT_USER,
  DELETE_USER,
  RESET_USER_PASSWORD,
  ADD_USER_PAYMENT,
  EDIT_USER_PAYMENT,
  DELETE_USER_PAYMENT,
  ENABLE_USER_PAYMENT_BACKDATE_ENTRY,
  VIEW_ROLE,
  CREATE_ROLE,
  EDIT_ROLE,
  DELETE_ROLE,
  EXPORT_USER,
  VIEW_USER_STATEMENT,
  EXPORT_USER_STATEMENT,
  // Finance
  FINANCE_MODULE,
  VIEW_FINANCE_CATEGORY,
  CREATE_FINANCE_CATEGORY,
  EDIT_FINANCE_CATEGORY,
  DELETE_FINANCE_CATEGORY,
  VIEW_EXPENSE,
  CREATE_EXPENSE,
  EDIT_EXPENSE,
  DELETE_EXPENSE,
  ENABLE_EXPENSE_BACKDATE_ENTRY,
  // Account
  ACCOUNT_MODULE,
  VIEW_ACCOUNT,
  CREATE_ACCOUNT,
  EDIT_ACCOUNT,
  DELETE_ACCOUNT,
  ADD_ACCOUNT_TRANSACTION,
  EDIT_ACCOUNT_TRANSACTION,
  DELETE_ACCOUNT_TRANSACTION,
  ENABLE_ACCOUNT_PAYMENT_BACKDATE_ENTRY,
  EXPORT_ACCOUNT_TRANSACTION,
  VIEW_ACCOUNT_LEDGER_MODE_STATEMENT,
  // Task
  TASK_MODULE,
  VIEW_ALL_TASK,
  VIEW_ASSIGNED_TASK,
  CREATE_TASK,
  EDIT_TASK,
  DELETE_TASK,
  VIEW_TASK_PROGRESS,
  CREATE_TASK_PROGRESS,
  EDIT_TASK_PROGRESS,
  DELETE_TASK_PROGRESS,
  // Tool
  TOOL_MODULE,
  VIEW_TOOL,
  CREATE_TOOL,
  EDIT_TOOL,
  DELETE_TOOL,
  MOVE_TOOL,
  UPDATE_STOCK,
  EXPORT_TOOL,
  // Performance Guarantee
  PERFORMANCE_GUARANTEE_MODULE,
  VIEW_PERFORMANCE_GUARANTEE,
  CREATE_PERFORMANCE_GUARANTEE,
  EDIT_PERFORMANCE_GUARANTEE,
  DELETE_PERFORMANCE_GUARANTEE,
  RELEASE_PERFORMANCE_GUARANTEE,
  EXPORT_PERFORMANCE_GUARANTEE,
  // Mobile Analytics
  MOBILE_ANALYTICS_MODULE,
  VIEW_BUDGET_OVERVIEW,
  VIEW_PROJECT_OVERVIEW,
  // Invoice
  INVOICE_MODULE,
  VIEW_GOVERNMENT_INVOICE,
  CREATE_GOVERNMENT_INVOICE,
  EDIT_GOVERNMENT_INVOICE,
  DELETE_GOVERNMENT_INVOICE,
  VIEW_CUSTOM_INVOICE,
  CREATE_CUSTOM_INVOICE,
  EDIT_CUSTOM_INVOICE,
  DELETE_CUSTOM_INVOICE,
  // Report
  REPORT_MODULE,
  GENERATE_CRM_REPORT,
  GENERATE_PROJECT_REPORT,
  GENERATE_CLIENT_REPORT,
  GENERATE_EMPLOYEE_REPORT,
  GENERATE_WORKER_REPORT,
  GENERATE_SUBCONTRACTOR_REPORT,
  GENERATE_VENDOR_REPORT,
  GENERATE_PURCHASE_REPORT,
  GENERATE_MATERIAL_REPORT,
  GENERATE_USER_REPORT,
  GENERATE_EXPENSE_REPORT,
  GENERATE_ACCOUNT_REPORT,
  GENERATE_DAILY_REPORT,
  GENERATE_TOOL_REPORT,
  // Office
  OFFICE_MODULE,
  VIEW_OFFICE_DASHBOARD,
  VIEW_OFFICE_BUDGET_OVERVIEW,
  VIEW_OFFICE_DOCUMENTS,
  EXPORT_OFFICE_REPORT,
  SELECT_OFFICE_ACCOUNT_IN_ENTRIES,
} from "../../constants/permissions";

export interface ModuleGroup {
  /** Header text for the group / feature row. */
  label: string;
  /** Feature codes belonging to this group (leaf checkboxes when expandable). */
  permissionCodes: string[];
  /** `false` renders only the header checkbox row (no chevron, no children). Default `true`. */
  expandable?: boolean;
  /** Checking any code here force-unchecks any active code listed in these peers. */
  mutuallyExclusiveWith?: string[];
  /** Group is disabled (and its codes auto-unchecked) unless one of these codes is active. */
  dependsOn?: string[];
  /** Grey helper text under the group label. */
  dependencyHint?: string;
}

export interface ModuleConfig {
  code: string;
  name: string;
  groups?: ModuleGroup[];
}

export interface SelectedPermission {
  id?: number;
  code: string;
  active?: boolean;
}

const CRM_LEAD_DEP_HINT = "Includes View All Leads or View Assigned Leads";

const DASHBOARD_GROUPS: ModuleGroup[] = [
  { label: "View Balance and Advance Summary", permissionCodes: [VIEW_BALANCE_AND_ADVANCE_SUMMARY], expandable: false },
  { label: "View Project Summary", permissionCodes: [VIEW_PROJECT_SUMMARY], expandable: false },
  { label: "View Expense Summary", permissionCodes: [VIEW_EXPENSE_SUMMARY], expandable: false },
];

const DASHBOARD_PRO_GROUPS: ModuleGroup[] = [
  { label: "View Balance and Advance Summary", permissionCodes: [VIEW_PRO_BALANCE_AND_ADVANCE_SUMMARY], expandable: false },
  { label: "View Project Summary", permissionCodes: [VIEW_PRO_PROJECT_SUMMARY], expandable: false },
  { label: "View Expense Summary", permissionCodes: [VIEW_PRO_EXPENSE_SUMMARY], expandable: false },
  { label: "View P&L Summary", permissionCodes: [VIEW_PRO_PNL_SUMMARY], expandable: false },
  { label: "View Daily Transactions", permissionCodes: [VIEW_PRO_DAILY_TRANSACTIONS], expandable: false },
  { label: "View Delayed Projects", permissionCodes: [VIEW_PRO_DELAYED_PROJECTS], expandable: false },
  { label: "View Monthly Income & Expense", permissionCodes: [VIEW_PRO_MONTHLY_INCOME_EXPENSE], expandable: false },
  { label: "View Task Summary", permissionCodes: [VIEW_PRO_TASK_SUMMARY], expandable: false },
];

const CRM_GROUPS: ModuleGroup[] = [
  {
    label: "View All Leads",
    permissionCodes: [CRM_VIEW_ALL_LEAD],
    mutuallyExclusiveWith: [CRM_VIEW_ASSIGNED_LEAD],
    expandable: false,
  },
  {
    label: "View Assigned Leads",
    permissionCodes: [CRM_VIEW_ASSIGNED_LEAD],
    mutuallyExclusiveWith: [CRM_VIEW_ALL_LEAD],
    expandable: false,
  },
  {
    label: "Manage Leads",
    permissionCodes: [CRM_CREATE_LEAD, CRM_EDIT_LEAD, CRM_DELETE_LEAD, CRM_EXPORT_LEAD],
    dependsOn: [CRM_VIEW_ALL_LEAD, CRM_VIEW_ASSIGNED_LEAD],
    dependencyHint: CRM_LEAD_DEP_HINT,
  },
  {
    label: "Lead Status Configuration",
    permissionCodes: [CRM_CREATE_LEAD_STATUS, CRM_EDIT_LEAD_STATUS, CRM_DELETE_LEAD_STATUS],
    dependsOn: [CRM_VIEW_ALL_LEAD, CRM_VIEW_ASSIGNED_LEAD],
    dependencyHint: CRM_LEAD_DEP_HINT,
  },
];

const CRM_PRO_GROUPS: ModuleGroup[] = [
  {
    label: "View All Leads",
    permissionCodes: [CRM_PRO_VIEW_ALL_LEAD],
    mutuallyExclusiveWith: [CRM_PRO_VIEW_ASSIGNED_LEAD],
    expandable: false,
  },
  {
    label: "View Assigned Leads",
    permissionCodes: [CRM_PRO_VIEW_ASSIGNED_LEAD],
    mutuallyExclusiveWith: [CRM_PRO_VIEW_ALL_LEAD],
    expandable: false,
  },
  {
    label: "Manage Leads",
    permissionCodes: [CRM_PRO_CREATE_LEAD, CRM_PRO_EDIT_LEAD, CRM_PRO_DELETE_LEAD, CRM_PRO_EXPORT_LEAD],
    dependsOn: [CRM_PRO_VIEW_ALL_LEAD, CRM_PRO_VIEW_ASSIGNED_LEAD],
    dependencyHint: CRM_LEAD_DEP_HINT,
  },
  {
    label: "Lead Status Configuration",
    permissionCodes: [CRM_PRO_CREATE_LEAD_STATUS, CRM_PRO_EDIT_LEAD_STATUS, CRM_PRO_DELETE_LEAD_STATUS],
    dependsOn: [CRM_PRO_VIEW_ALL_LEAD, CRM_PRO_VIEW_ASSIGNED_LEAD],
    dependencyHint: CRM_LEAD_DEP_HINT,
  },
  {
    label: "Task Management",
    permissionCodes: [CRM_PRO_CREATE_TASK, CRM_PRO_EDIT_TASK, CRM_PRO_DELETE_TASK, CRM_PRO_UPDATE_TASK_PROGRESS],
    dependsOn: [CRM_PRO_VIEW_ALL_LEAD, CRM_PRO_VIEW_ASSIGNED_LEAD],
    dependencyHint: CRM_LEAD_DEP_HINT,
  },
  {
    label: "Document Management",
    permissionCodes: [CRM_PRO_UPLOAD_DOCUMENT, CRM_PRO_DELETE_DOCUMENT],
    dependsOn: [CRM_PRO_VIEW_ALL_LEAD, CRM_PRO_VIEW_ASSIGNED_LEAD],
    dependencyHint: CRM_LEAD_DEP_HINT,
  },
];

const PROJECT_GROUPS: ModuleGroup[] = [
  { label: "View Projects", permissionCodes: [VIEW_PROJECT], expandable: false },
  {
    label: "Manage Projects",
    permissionCodes: [CREATE_PROJECT, EDIT_PROJECT, DELETE_PROJECT, EXPORT_PROJECT],
    dependsOn: [VIEW_PROJECT],
    dependencyHint: "Includes View Projects",
  },
  {
    label: "Manage Project Stages",
    permissionCodes: [VIEW_STAGE, CREATE_STAGE, EDIT_STAGE, DELETE_STAGE],
    dependsOn: [VIEW_PROJECT],
    dependencyHint: "Includes View Project",
  },
];

const CLIENT_GROUPS: ModuleGroup[] = [
  { label: "View Clients", permissionCodes: [VIEW_CLIENT], expandable: false },
  {
    label: "Manage Clients",
    permissionCodes: [CREATE_CLIENT, EDIT_CLIENT, DELETE_CLIENT],
    dependsOn: [VIEW_CLIENT],
    dependencyHint: "Includes View Clients",
  },
  {
    label: "Manage Client Financial Operations",
    permissionCodes: [
      ADD_CLIENT_INCOME,
      EDIT_CLIENT_INCOME,
      DELETE_CLIENT_INCOME,
      ENABLE_CLIENT_INCOME_BACKDATE_ENTRY,
      ADD_CLIENT_PAYMENT,
      EDIT_CLIENT_PAYMENT,
      DELETE_CLIENT_PAYMENT,
      ENABLE_CLIENT_PAYMENT_BACKDATE_ENTRY,
    ],
  },
  {
    label: "Client Statement and Export",
    permissionCodes: [VIEW_CLIENT_STATEMENT, VIEW_CLIENT_LEDGER_MODE_STATEMENT, EXPORT_CLIENT, EXPORT_CLIENT_STATEMENT],
    dependsOn: [VIEW_CLIENT],
    dependencyHint: "Includes View Clients",
  },
];

const EMPLOYEE_GROUPS: ModuleGroup[] = [
  { label: "View Employees", permissionCodes: [VIEW_EMPLOYEE], expandable: false },
  {
    label: "Manage Employees",
    permissionCodes: [CREATE_EMPLOYEE, EDIT_EMPLOYEE, DELETE_EMPLOYEE],
    dependsOn: [VIEW_EMPLOYEE],
    dependencyHint: "Includes View Employees",
  },
  {
    label: "Manage Employee Attendance",
    permissionCodes: [ADD_EMPLOYEE_ATTENDANCE, EDIT_EMPLOYEE_ATTENDANCE, DELETE_EMPLOYEE_ATTENDANCE],
    dependsOn: [VIEW_EMPLOYEE],
    dependencyHint: "Includes View Employees",
  },
  {
    label: "Manage Employee Financial Operations",
    permissionCodes: [
      ADD_EMPLOYEE_INCENTIVE,
      EDIT_EMPLOYEE_INCENTIVE,
      DELETE_EMPLOYEE_INCENTIVE,
      ENABLE_EMPLOYEE_SALARY_BACKDATE_ENTRY,
      ADD_EMPLOYEE_SALARY,
      EDIT_EMPLOYEE_SALARY,
      DELETE_EMPLOYEE_SALARY,
    ],
    dependsOn: [VIEW_EMPLOYEE],
    dependencyHint: "Includes View Employee",
  },
  {
    label: "Employee Statement and Exports",
    permissionCodes: [VIEW_EMPLOYEE_STATEMENT, EXPORT_EMPLOYEE, EXPORT_EMPLOYEE_STATEMENT],
    dependsOn: [VIEW_EMPLOYEE],
    dependencyHint: "Includes View Employee",
  },
];

const WORKER_GROUPS: ModuleGroup[] = [
  { label: "View Workers", permissionCodes: [VIEW_WORKER], expandable: false },
  {
    label: "Manage Workers",
    permissionCodes: [CREATE_WORKER, EDIT_WORKER, DELETE_WORKER],
    dependsOn: [VIEW_WORKER],
    dependencyHint: "Includes View Workers",
  },
  {
    label: "Work Management",
    permissionCodes: [
      ADD_WORK,
      ADD_BULK_WORK,
      EDIT_WORK,
      EDIT_BULK_WORK,
      DELETE_WORK,
      VIEW_GROUP_WORK,
      ADD_GROUP_WORK,
      EDIT_GROUP_WORK,
      DELETE_GROUP_WORK,
      ENABLE_WORK_BACKDATE_ENTRY,
    ],
  },
  {
    label: "Worker Payroll Management",
    permissionCodes: [
      ADD_WORKER_PAYMENT,
      EDIT_WORKER_PAYMENT,
      DELETE_WORKER_PAYMENT,
      ENABLE_WORKER_PAYMENT_BACKDATE_ENTRY,
      ENABLE_WORKER_ADVANCE_PAYMENT,
    ],
  },
  {
    label: "Worker Statements and Exports",
    permissionCodes: [VIEW_WORKER_STATEMENT, VIEW_WORKER_LEDGER_MODE_STATEMENT, EXPORT_WORKER_STATEMENT, EXPORT_WORKER],
  },
];

const WEEKLY_WORKER_PAYMENT_GROUPS: ModuleGroup[] = [
  { label: "View Weekly Payments", permissionCodes: [VIEW_WEEKLY_WORKER_PAYMENT], expandable: false },
  {
    label: "Manage Weekly Payments",
    permissionCodes: [ADD_WEEKLY_WORKER_PAYMENT, EDIT_WEEKLY_WORKER_PAYMENT, DELETE_WEEKLY_WORKER_PAYMENT],
    dependsOn: [VIEW_WEEKLY_WORKER_PAYMENT],
    dependencyHint: "Includes View Weekly Payments",
  },
  { label: "View Weekly Payment Report", permissionCodes: [VIEW_WEEKLY_WORKER_PAYMENT_REPORT], expandable: false },
  {
    label: "Weekly Payment Report Actions",
    permissionCodes: [PAY_WEEKLY_WORKER_PAYMENT, ASSIGN_WEEKLY_WORKER_PAYMENT_SUPERVISOR, EXPORT_WEEKLY_WORKER_PAYMENT],
    dependsOn: [VIEW_WEEKLY_WORKER_PAYMENT_REPORT],
    dependencyHint: "Requires View Weekly Payment Report",
  },
  { label: "View Worker Welfare", permissionCodes: [VIEW_WORKER_WELFARE], expandable: false },
  {
    label: "Manage Worker Welfare",
    permissionCodes: [CREATE_WORKER_WELFARE, EDIT_WORKER_WELFARE, PAY_WORKER_WELFARE],
    dependsOn: [VIEW_WORKER_WELFARE],
    dependencyHint: "Includes View Worker Welfare",
  },
];

const SUBCONTRACTOR_GROUPS: ModuleGroup[] = [
  { label: "View Subcontractors", permissionCodes: [VIEW_SUBCONTRACTOR], expandable: false },
  {
    label: "Manage Subcontractors",
    permissionCodes: [CREATE_SUBCONTRACTOR, EDIT_SUBCONTRACTOR, DELETE_SUBCONTRACTOR],
    dependsOn: [VIEW_SUBCONTRACTOR],
    dependencyHint: "Includes View Subcontractors",
  },
  {
    label: "Contract Management",
    permissionCodes: [VIEW_CONTRACT, CREATE_CONTRACT, EDIT_CONTRACT, DELETE_CONTRACT, ENABLE_CONTRACT_BACKDATE_ENTRY],
    dependsOn: [VIEW_SUBCONTRACTOR],
    dependencyHint: "Includes View Subcontractors",
  },
  {
    label: "Subcontractor Transactions",
    permissionCodes: [
      CREATE_SUBCONTRACTOR_TRANSACTION,
      EDIT_SUBCONTRACTOR_TRANSACTION,
      DELETE_SUBCONTRACTOR_TRANSACTION,
      VIEW_SUBCONTRACTOR_STATEMENT,
      VIEW_SUBCONTRACTOR_LEDGER_MODE_STATEMENT,
      ENABLE_SUBCONTRACTOR_TRANSACTION_BACKDATE_ENTRY,
      ENABLE_SUBCONTRACTOR_ADVANCE_PAYMENT,
    ],
  },
  {
    label: "Subcontractor Statement and Exports",
    permissionCodes: [EXPORT_SUBCONTRACTOR, EXPORT_SUBCONTRACTOR_TRANSACTION, EXPORT_CONTRACT],
  },
];

const VENDOR_GROUPS: ModuleGroup[] = [
  { label: "View Vendors", permissionCodes: [VIEW_VENDOR], expandable: false },
  {
    label: "Manage Vendors",
    permissionCodes: [CREATE_VENDOR, EDIT_VENDOR, DELETE_VENDOR],
    dependsOn: [VIEW_VENDOR],
    dependencyHint: "Includes View Vendors",
  },
  {
    label: "Purchase Management",
    permissionCodes: [
      CREATE_PURCHASE,
      EDIT_PURCHASE,
      RETURN_PURCHASE,
      DELETE_PURCHASE,
      DELETE_PURCHASE_RETURN,
      EXPORT_PURCHASE,
      ENABLE_PURCHASE_BACKDATE_ENTRY,
    ],
  },
  {
    label: "Material Management",
    permissionCodes: [VIEW_MATERIAL, CREATE_MATERIAL, EDIT_MATERIAL, DELETE_MATERIAL],
  },
  {
    label: "Vendor Payments",
    permissionCodes: [
      ADD_VENDOR_PAYMENT,
      EDIT_VENDOR_PAYMENT,
      DELETE_VENDOR_PAYMENT,
      ENABLE_VENDOR_PAYMENT_BACKDATE_ENTRY,
      ENABLE_VENDOR_ADVANCE_PAYMENT,
    ],
  },
  {
    label: "Vendor Statements and Exports",
    permissionCodes: [VIEW_VENDOR_STATEMENT, VIEW_VENDOR_LEDGER_MODE_STATEMENT, EXPORT_VENDOR_STATEMENT, EXPORT_VENDOR],
  },
];

const USER_GROUPS: ModuleGroup[] = [
  { label: "View Users", permissionCodes: [VIEW_USER], expandable: false },
  {
    label: "Manage Users",
    permissionCodes: [CREATE_USER, EDIT_USER, DELETE_USER, RESET_USER_PASSWORD],
    dependsOn: [VIEW_USER],
    dependencyHint: "Includes View Users",
  },
  {
    label: "Manage User Payments",
    permissionCodes: [ADD_USER_PAYMENT, EDIT_USER_PAYMENT, DELETE_USER_PAYMENT, ENABLE_USER_PAYMENT_BACKDATE_ENTRY],
  },
  {
    label: "Role Management",
    permissionCodes: [VIEW_ROLE, CREATE_ROLE, EDIT_ROLE, DELETE_ROLE],
  },
  {
    label: "User Statement and Exports",
    permissionCodes: [EXPORT_USER, VIEW_USER_STATEMENT, EXPORT_USER_STATEMENT],
  },
];

const FINANCE_GROUPS: ModuleGroup[] = [
  { label: "View Expense Category", permissionCodes: [VIEW_FINANCE_CATEGORY], expandable: false },
  {
    label: "Manage Expense Category",
    permissionCodes: [CREATE_FINANCE_CATEGORY, EDIT_FINANCE_CATEGORY, DELETE_FINANCE_CATEGORY],
    dependsOn: [VIEW_FINANCE_CATEGORY],
  },
  { label: "View Expense", permissionCodes: [VIEW_EXPENSE], expandable: false },
  {
    label: "Manage Expense",
    permissionCodes: [CREATE_EXPENSE, EDIT_EXPENSE, DELETE_EXPENSE, ENABLE_EXPENSE_BACKDATE_ENTRY],
    dependsOn: [VIEW_EXPENSE],
  },
];

const ACCOUNT_GROUPS: ModuleGroup[] = [
  { label: "View Accounts", permissionCodes: [VIEW_ACCOUNT], expandable: false },
  {
    label: "Manage Accounts",
    permissionCodes: [CREATE_ACCOUNT, EDIT_ACCOUNT, DELETE_ACCOUNT],
    dependsOn: [VIEW_ACCOUNT],
    dependencyHint: "Includes View Accounts",
  },
  {
    label: "Account Transfers",
    permissionCodes: [
      ADD_ACCOUNT_TRANSACTION,
      EDIT_ACCOUNT_TRANSACTION,
      DELETE_ACCOUNT_TRANSACTION,
      ENABLE_ACCOUNT_PAYMENT_BACKDATE_ENTRY,
      EXPORT_ACCOUNT_TRANSACTION,
      VIEW_ACCOUNT_LEDGER_MODE_STATEMENT,
    ],
  },
];

const TASK_GROUPS: ModuleGroup[] = [
  { label: "View All Tasks", permissionCodes: [VIEW_ALL_TASK], expandable: false },
  { label: "View Assigned Tasks", permissionCodes: [VIEW_ASSIGNED_TASK], expandable: false },
  {
    label: "Manage Tasks",
    permissionCodes: [CREATE_TASK, EDIT_TASK, DELETE_TASK],
    dependsOn: [VIEW_ALL_TASK, VIEW_ASSIGNED_TASK],
    dependencyHint: "Includes View Tasks or View Assigned Tasks",
  },
  {
    label: "Task Progress Tracking",
    permissionCodes: [VIEW_TASK_PROGRESS, CREATE_TASK_PROGRESS, EDIT_TASK_PROGRESS, DELETE_TASK_PROGRESS],
    dependsOn: [VIEW_ALL_TASK, VIEW_ASSIGNED_TASK],
    dependencyHint: "Includes View Tasks or View Assigned Tasks",
  },
];

const TOOL_GROUPS: ModuleGroup[] = [
  { label: "View Tools", permissionCodes: [VIEW_TOOL], expandable: false },
  {
    label: "Manage Tools",
    permissionCodes: [CREATE_TOOL, EDIT_TOOL, DELETE_TOOL, MOVE_TOOL, UPDATE_STOCK, EXPORT_TOOL],
    dependsOn: [VIEW_TOOL],
    dependencyHint: "Includes View Tools",
  },
];

const PG_GROUPS: ModuleGroup[] = [
  { label: "View PG", permissionCodes: [VIEW_PERFORMANCE_GUARANTEE], expandable: false },
  {
    label: "Manage PG",
    permissionCodes: [
      CREATE_PERFORMANCE_GUARANTEE,
      EDIT_PERFORMANCE_GUARANTEE,
      DELETE_PERFORMANCE_GUARANTEE,
      RELEASE_PERFORMANCE_GUARANTEE,
      EXPORT_PERFORMANCE_GUARANTEE,
    ],
    dependsOn: [VIEW_PERFORMANCE_GUARANTEE],
    dependencyHint: "Includes View PG",
  },
];

const MOBILE_ANALYTICS_GROUPS: ModuleGroup[] = [
  { label: "View Budget Overview", permissionCodes: [VIEW_BUDGET_OVERVIEW], expandable: false },
  { label: "View Project Overview", permissionCodes: [VIEW_PROJECT_OVERVIEW], expandable: false },
];

const INVOICE_GROUPS: ModuleGroup[] = [
  { label: "View Government Client Invoices", permissionCodes: [VIEW_GOVERNMENT_INVOICE], expandable: false },
  { label: "View Custom Client Invoices", permissionCodes: [VIEW_CUSTOM_INVOICE], expandable: false },
  {
    label: "Manage Government Client Invoices",
    permissionCodes: [CREATE_GOVERNMENT_INVOICE, EDIT_GOVERNMENT_INVOICE, DELETE_GOVERNMENT_INVOICE],
    dependsOn: [VIEW_GOVERNMENT_INVOICE],
    dependencyHint: "Includes View Government Client Invoices",
  },
  {
    label: "Manage Custom Client Invoices",
    permissionCodes: [CREATE_CUSTOM_INVOICE, EDIT_CUSTOM_INVOICE, DELETE_CUSTOM_INVOICE],
    dependsOn: [VIEW_CUSTOM_INVOICE],
    dependencyHint: "Includes View Custom Client Invoices",
  },
];

const REPORT_GROUPS: ModuleGroup[] = [
  { label: "View CRM Report", permissionCodes: [GENERATE_CRM_REPORT], expandable: false },
  { label: "View Project Report", permissionCodes: [GENERATE_PROJECT_REPORT], expandable: false },
  { label: "View Client Report", permissionCodes: [GENERATE_CLIENT_REPORT], expandable: false },
  { label: "View Employee Report", permissionCodes: [GENERATE_EMPLOYEE_REPORT], expandable: false },
  { label: "View Worker Report", permissionCodes: [GENERATE_WORKER_REPORT], expandable: false },
  { label: "View Subcontractor Report", permissionCodes: [GENERATE_SUBCONTRACTOR_REPORT], expandable: false },
  { label: "View Vendor Report", permissionCodes: [GENERATE_VENDOR_REPORT], expandable: false },
  { label: "View Purchase Report", permissionCodes: [GENERATE_PURCHASE_REPORT], expandable: false },
  { label: "View Material Report", permissionCodes: [GENERATE_MATERIAL_REPORT], expandable: false },
  { label: "View User Report", permissionCodes: [GENERATE_USER_REPORT], expandable: false },
  { label: "View Expense Report", permissionCodes: [GENERATE_EXPENSE_REPORT], expandable: false },
  { label: "View Account Report", permissionCodes: [GENERATE_ACCOUNT_REPORT], expandable: false },
  { label: "View Daily Report", permissionCodes: [GENERATE_DAILY_REPORT], expandable: false },
  { label: "View Tool Report", permissionCodes: [GENERATE_TOOL_REPORT], expandable: false },
];

const OFFICE_GROUPS: ModuleGroup[] = [
  { label: "View Office Account", permissionCodes: [VIEW_OFFICE_DASHBOARD], expandable: false },
  {
    label: "Select Office Account in Entries",
    permissionCodes: [SELECT_OFFICE_ACCOUNT_IN_ENTRIES],
    dependsOn: [VIEW_OFFICE_DASHBOARD],
    dependencyHint: "Requires View Office Account",
  },
  {
    label: "Office Income",
    permissionCodes: [ADD_CLIENT_INCOME, EDIT_CLIENT_INCOME, DELETE_CLIENT_INCOME],
    dependsOn: [VIEW_OFFICE_DASHBOARD],
    dependencyHint: "Requires View Office Account",
  },
  {
    label: "Office Expenses",
    permissionCodes: [VIEW_EXPENSE, CREATE_EXPENSE, EDIT_EXPENSE, DELETE_EXPENSE],
    dependsOn: [VIEW_OFFICE_DASHBOARD],
    dependencyHint: "Requires View Office Account",
  },
  {
    label: "Office Purchases",
    permissionCodes: [CREATE_PURCHASE, EDIT_PURCHASE, DELETE_PURCHASE],
    dependsOn: [VIEW_OFFICE_DASHBOARD],
    dependencyHint: "Requires View Office Account",
  },
  {
    label: "Office Overview",
    permissionCodes: [VIEW_OFFICE_BUDGET_OVERVIEW, VIEW_OFFICE_DOCUMENTS],
    dependsOn: [VIEW_OFFICE_DASHBOARD],
    dependencyHint: "Requires View Office Account",
  },
  {
    label: "Office Exports",
    permissionCodes: [EXPORT_OFFICE_REPORT],
    dependsOn: [VIEW_OFFICE_DASHBOARD],
    dependencyHint: "Requires View Office Account",
  },
];

/**
 * Ordered exactly as the role form renders the modules. A module is only shown when the API
 * reports it (and the current user has at least one of its features).
 */
export const MODULE_CONFIG: ModuleConfig[] = [
  { code: DASHBOARD_MODULE, name: "Dashboard", groups: DASHBOARD_GROUPS },
  { code: DASHBOARD_PRO_MODULE, name: "Dashboard Pro", groups: DASHBOARD_PRO_GROUPS },
  { code: CRM_MODULE, name: "CRM", groups: CRM_GROUPS },
  { code: CRM_PRO_MODULE, name: "CRM Pro", groups: CRM_PRO_GROUPS },
  { code: PROJECT_MODULE, name: "Project", groups: PROJECT_GROUPS },
  { code: CLIENT_MODULE, name: "Clients", groups: CLIENT_GROUPS },
  { code: EMPLOYEE_MODULE, name: "Employees", groups: EMPLOYEE_GROUPS },
  { code: WORKER_MODULE, name: "Workers", groups: WORKER_GROUPS },
  { code: WEEKLY_WORKER_PAYMENT_MODULE, name: "Weekly Worker Payment", groups: WEEKLY_WORKER_PAYMENT_GROUPS },
  { code: SUBCONTRACTOR_MODULE, name: "Subcontractors", groups: SUBCONTRACTOR_GROUPS },
  { code: VENDOR_MODULE, name: "Vendors", groups: VENDOR_GROUPS },
  { code: USER_MODULE, name: "Users & Roles", groups: USER_GROUPS },
  { code: FINANCE_MODULE, name: "Expenses", groups: FINANCE_GROUPS },
  { code: ACCOUNT_MODULE, name: "Accounts", groups: ACCOUNT_GROUPS },
  { code: TASK_MODULE, name: "Task Management", groups: TASK_GROUPS },
  { code: TOOL_MODULE, name: "Tools", groups: TOOL_GROUPS },
  { code: PERFORMANCE_GUARANTEE_MODULE, name: "PG", groups: PG_GROUPS },
  { code: MOBILE_ANALYTICS_MODULE, name: "Mobile Analytics", groups: MOBILE_ANALYTICS_GROUPS },
  { code: INVOICE_MODULE, name: "Invoices", groups: INVOICE_GROUPS },
  { code: REPORT_MODULE, name: "Reports", groups: REPORT_GROUPS },
  { code: OFFICE_MODULE, name: "Office Accounts", groups: OFFICE_GROUPS },
];

const isActiveOrHidden = (code: string, state: SelectedPermission[], hiddenFeatureCodes: string[]) =>
  hiddenFeatureCodes.includes(code) || state.some((p) => p.code === code && p.active);

/**
 * After a bulk "Select All", settle every mutually-exclusive pair so only one side stays
 * active. The first code encountered wins; its peers are switched off.
 */
export const enforceGlobalSelectAllRules = (permissionsState: SelectedPermission[]): SelectedPermission[] => {
  const nextState = [...permissionsState];

  MODULE_CONFIG.forEach((mc) => {
    mc.groups?.forEach((group) => {
      if (!group.mutuallyExclusiveWith) return;
      group.permissionCodes.forEach((code) => {
        const current = nextState.find((x) => x.code === code);
        if (!current?.active) return;
        group.mutuallyExclusiveWith?.forEach((exclusiveCode) => {
          const idx = nextState.findIndex((x) => x.code === exclusiveCode);
          if (idx >= 0 && nextState[idx].active) {
            nextState[idx] = { ...nextState[idx], active: false };
          }
        });
      });
    });
  });

  return nextState;
};

/**
 * Force-uncheck any group in `groups` whose `dependsOn` is no longer satisfied (auto-cascade).
 * Runs after every mutation so unchecking a "View X" permission clears the groups that
 * required it.
 */
export const enforceDependencies = (
  permissionsState: SelectedPermission[],
  groups: ModuleGroup[],
  hiddenFeatureCodes: string[] = [],
): SelectedPermission[] => {
  const updated = [...permissionsState];

  groups.forEach((group) => {
    if (!group.dependsOn?.length) return;
    const dependencyMet = group.dependsOn.some((depCode) => isActiveOrHidden(depCode, updated, hiddenFeatureCodes));
    if (dependencyMet) return;
    group.permissionCodes.forEach((code) => {
      const idx = updated.findIndex((p) => p.code === code);
      if (idx >= 0 && updated[idx].active) {
        updated[idx] = { ...updated[idx], active: false };
      }
    });
  });

  return updated;
};

const ALL_GROUPS: ModuleGroup[] = MODULE_CONFIG.flatMap((mc) => mc.groups ?? []);

/** `enforceDependencies` across every module — used for form-level "Select All". */
export const enforceGlobalDependencies = (
  permissionsState: SelectedPermission[],
  hiddenFeatureCodes: string[] = [],
): SelectedPermission[] => enforceDependencies(permissionsState, ALL_GROUPS, hiddenFeatureCodes);
